import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowRight, RotateCw } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { cn } from '@/shared/lib/cn';
import { swapPorIngresar, type InventarioRow } from '../api';

/**
 * Corrección manual (solo admin): mueve unidades de `Por Ingresar` a `Disponible`.
 * Lo normal es que el stock pase solo al registrar una entrega en Recepción;
 * esto sirve para arreglar descuadres. Mirrors
 * PowerApps's PopUp_SwapInventoryItem (Screen_Inventario.pa.yaml:2400+).
 *
 * Total Stock is unchanged — this is an internal reshuffle.
 */
export function SwapDialog({
  row,
  onClose,
}: {
  row: InventarioRow;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [cantidadRaw, setCantidadRaw] = useState('');

  const cantidad = parseCantidad(cantidadRaw);
  const porIngresar = row.buckets.porIngresar;
  const exceeds = cantidad !== null && cantidad > porIngresar + 0.0001;
  const canSubmit =
    cantidad !== null && cantidad > 0 && !exceeds && porIngresar > 0;

  const mutation = useMutation({
    mutationFn: () =>
      swapPorIngresar(row.id, {
        cantidad: cantidad ?? 0,
      }),
    onSuccess: (r) => {
      if (r.outcome === 'insufficient_por_ingresar') {
        toast.error(r.message);
        return;
      }
      toast.success(`${r.cantidad} un. pasaron a Disponible`);
      qc.invalidateQueries({ queryKey: ['inventario'] });
      onClose();
    },
    onError: () => {
      toast.error('No se pudo aplicar la corrección');
    },
  });

  return (
    <Dialog open onOpenChange={(o) => !o && !mutation.isPending && onClose()}>
      <DialogContent
        className="max-w-md"
        processing={mutation.isPending && 'Aplicando corrección…'}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RotateCw className="h-4 w-4" aria-hidden />
            Corrección: Por ingresar → Disponible
          </DialogTitle>
          <DialogDescription>
            {row.fields.Articulo_I || 'artículo'} · Por Ingresar actual:{' '}
            <strong>{porIngresar}</strong> un.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <div className="flex items-center justify-between gap-3 rounded-lg border bg-muted/30 px-3 py-2">
            <Bucket label="Por Ingresar" value={porIngresar} tone="sky" />
            <ArrowRight className="h-5 w-5 text-muted-foreground" aria-hidden />
            <Bucket label="Disponible" value={row.buckets.disponible} tone="green" />
          </div>

          <div className="space-y-1">
            <Label htmlFor="swap-cantidad" className="text-sm">
              Cantidad a mover <span className="text-destructive">*</span>
            </Label>
            <Input
              id="swap-cantidad"
              type="number"
              inputMode="decimal"
              min={0}
              max={porIngresar}
              step="0.01"
              placeholder={`máx ${porIngresar}`}
              value={cantidadRaw}
              onChange={(e) => setCantidadRaw(e.target.value)}
              className={cn('tabular-nums', exceeds && 'border-destructive')}
              autoFocus
            />
            {exceeds && (
              <p className="text-xs text-destructive">
                Excede el stock Por Ingresar disponible ({porIngresar}).
              </p>
            )}
          </div>

          {cantidad !== null && cantidad > 0 && !exceeds && (
            <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
              Después de la corrección: Por Ingresar {porIngresar - cantidad} · Disponible{' '}
              {row.buckets.disponible + cantidad}
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button
            onClick={() => mutation.mutate()}
            disabled={!canSubmit || mutation.isPending}
            className="gap-2"
          >
            <RotateCw className="h-4 w-4" aria-hidden />
            Confirmar corrección
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Bucket({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: 'sky' | 'green';
}) {
  const cls =
    tone === 'sky'
      ? 'bg-sky-100 text-sky-700'
      : 'bg-emerald-100 text-emerald-700';
  return (
    <div className="flex flex-1 flex-col items-center">
      <span className="text-[11px] uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <span
        className={cn(
          'mt-1 inline-flex min-w-12 justify-center rounded-md px-2 py-0.5 text-sm font-semibold tabular-nums',
          cls,
        )}
      >
        {value}
      </span>
    </div>
  );
}

function parseCantidad(raw: string): number | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const n = Number(trimmed.replace(',', '.'));
  return Number.isFinite(n) && n >= 0 ? n : null;
}
